import React from 'react';
import { Button } from '../ui/button';
import { ArrowRight } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * DualCTA Component
 * 
 * Displays two call-to-action buttons side-by-side (desktop) or stacked (mobile).
 * Used in the final Decision scene (Scene 5) with "Start Free" and "Get Analyzed" options.
 * 
 * @param {Object} props
 * @param {Function} props.onStartFree - Handler for "Start Free" button
 * @param {Function} props.onGetAnalyzed - Handler for "Get Analyzed" button
 * @param {boolean} props.loading - Show loading state
 * @param {string} props.className - Additional Tailwind classes
 * @returns {JSX.Element}
 */
export const DualCTA = ({
  onStartFree = () => console.log('Start Free clicked'),
  onGetAnalyzed = () => console.log('Get Analyzed clicked'),
  loading = false,
  className = '',
}) => {
  return (
    <div
      className={cn(
        'flex flex-col sm:flex-row',
        'items-stretch sm:items-center',
        'gap-3 sm:gap-4',
        'w-full sm:w-auto',
        className
      )}
      data-testid="dual-cta-container"
    >
      {/* Primary CTA: Start Free */}
      <Button
        onClick={onStartFree}
        disabled={loading}
        size="lg"
        className={cn(
          'h-14 px-8',
          'text-base font-medium',
          'bg-teal-400 hover:bg-teal-500',
          'text-slate-900',
          'shadow-md hover:shadow-glow-teal',
          'transition-all duration-300',
          'group',
          'min-w-[200px]'
        )}
        data-testid="start-free-cta-button"
      >
        <span className="flex items-center gap-2">
          Start Free
          <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
        </span>
      </Button>

      {/* Secondary CTA: Get Analyzed */}
      <Button
        onClick={onGetAnalyzed}
        disabled={loading}
        size="lg"
        variant="outline"
        className={cn(
          'h-14 px-8',
          'text-base font-medium',
          'bg-white hover:bg-gray-50',
          'border-2 border-navy/30 hover:border-navy/50',
          'text-navy',
          'shadow-md hover:shadow-lg',
          'transition-all duration-300',
          'group',
          'min-w-[200px]'
        )}
        data-testid="get-analyzed-cta-button"
      >
        <span className="flex items-center gap-2">
          Get Analyzed – $2,499
          <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
        </span>
      </Button>
    </div>
  );
};

/**
 * CTADetails Component
 * 
 * Displays feature lists below each CTA button.
 * Used in Scene 5 to show benefits of each option.
 */
export const CTADetails = ({ type = 'free' }) => {
  const freeFeatures = [
    'Explore strategies',
    'No credit card required',
    'Educational resources',
  ];

  const paidFeatures = [
    'CPA-backed report',
    '2× ROI or refund',
    'Personalized action plan',
  ];

  const features = type === 'free' ? freeFeatures : paidFeatures;

  return (
    <ul
      className="mt-3 space-y-1.5 text-sm text-navy/70"
      data-testid={`cta-details-${type}`}
    >
      {features.map((feature, index) => (
        <li key={index} className="flex items-center gap-2">
          <span className="text-teal-500">✓</span>
          {feature}
        </li>
      ))}
    </ul>
  );
};